import { useCallback, MouseEvent } from "react";

import { useUnifiedRegistry } from "../unified-registry-context";
import { RegistryItem } from "../types";

import { isModifierKeyPressed } from "@/utils/shortcuts";

export const useItemSelection = (onItemClick?: (item: RegistryItem) => void) => {
  const registry = useUnifiedRegistry();

  const handleItemClick = useCallback(
    (event: MouseEvent, item: RegistryItem): boolean => {
      const entity = item;

      // Skip selection logic if disabled
      if (registry.selectionDisabled) {
        // Still handle custom onClick if provided
        if (onItemClick) {
          event.preventDefault();
          onItemClick(item);
          return true;
        }
        return false;
      }

      // Check if CMD (Mac) or CTRL (Windows/Linux) is pressed for multi-select
      const isMultiSelect = isModifierKeyPressed(event);

      if (isMultiSelect) {
        event.preventDefault(); // Prevent default browser behavior (like opening in new tab)
        event.stopPropagation(); // Stop event bubbling

        registry.toggleEntitySelection(entity);
        return true; // Indicate that selection was handled
      }

      // In multiSelectMode, don't clear selection on regular clicks
      // Clear selection on regular click (unless shift is held for future range selection)
      if (!registry.multiSelectMode && !event.shiftKey) {
        registry.clearSelection();
      }

      // If onItemClick is provided, handle it and prevent default navigation
      if (onItemClick) {
        event.preventDefault();
        onItemClick(item);
        return true; // Indicate that click was handled
      }

      return false; // Indicate normal navigation should proceed
    },
    [registry, onItemClick],
  );

  const isSelected = useCallback(
    (item: RegistryItem): boolean => registry.isSelected(item),
    [registry],
  );

  const selectItem = useCallback(
    (item: RegistryItem) => {
      registry.selectEntity(item);
    },
    [registry],
  );

  const deselectItem = useCallback(
    (item: RegistryItem) => {
      registry.deselectEntity(item);
    },
    [registry],
  );

  const toggleItem = useCallback(
    (item: RegistryItem) => {
      registry.toggleEntitySelection(item);
    },
    [registry],
  );

  return {
    handleItemClick,
    isSelected,
    selectItem,
    deselectItem,
    toggleItem,
    selectedCount: registry.selectedCount,
    selectedEntities: registry.selectedEntities,
    clearSelection: registry.clearSelection,
  };
};
